import { useMemo } from 'react';
import {
  Badge,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  TextFieldProps,
  debounce,
} from '@mui/material';

import { Iconify } from '../iconify';

export type SearchBarProps = TextFieldProps & {
  filterShown?: boolean;
  isFilterApplied?: boolean;
  onClickFilter?: () => void;
  activeDebounce?: boolean;
};

const SearchBar = ({
  sx,
  filterShown,
  onClickFilter,
  activeDebounce = true,
  onChange,
  isFilterApplied = false,
  ...rest
}: SearchBarProps) => {
  const searchDelayed = useMemo(
    () => (activeDebounce && onChange ? debounce(onChange, 300) : onChange),
    [onChange, activeDebounce]
  );

  return (
    <Stack direction="row" sx={sx} spacing={1} alignItems={'center'}>
      <TextField
        fullWidth
        onChange={searchDelayed}
        {...rest}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton>
                <Iconify icon="eva:search-fill" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          height: '100%',
          '.MuiOutlinedInput-root': {
            height: '100%',
            borderRadius: '50px',
            backgroundColor: 'common.grey',
          },
        }}
      />

      {filterShown && (
        <IconButton
          onClick={onClickFilter}
          sx={{
            p: 0,
            width: 40,
            height: 40,
            backgroundColor: (theme) => theme.palette.primary.main,
            color: (theme) => theme.palette.common.white,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
          }}
        >
          <Badge variant="dot" color="secondary" invisible={!isFilterApplied}>
            <Stack height={25} width={25} alignItems="center" justifyContent="center">
              <Iconify icon={'uil:filter'} width={20} />
            </Stack>
          </Badge>
        </IconButton>
      )}
    </Stack>
  );
};

export default SearchBar;
